/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';
/**
 * @typedef {Object} LayoutSectionDelimiterValidation
 * @property {Boolean}        isValid             - Layout has valid section delimiters.
 * @property {Number}         count               - Total number of section delimiters in layout.
 * @property {Array.<Object>} rootDelimiters      - Identifying data for root section delimiters.
 * @property {Array.<Object>} nestedDelimiters    - Identifying data for nested section delimiters.
 * @property {Array.<Object>} malformedDelimiters - Identifying data for malformed root section delimiters.
 */

import { omit, without } from 'underscore';
import { createRowId } from 'DesignData/lib/normalization/layouts/identifiers';
import { forEachNormalizedWidgets } from 'DesignData/lib/normalization/layouts/iterators';
import { cloneNormalizedLayoutData } from 'DesignData/lib/normalization/layouts/utils';
import { LayoutWidget } from 'DesignData/models/layouts';
import SectionDelimiter from 'DesignData/models/widgets/SectionDelimiter';
import { isSectionDelimiterType } from 'DesignData/helpers/widgets';
import { byName } from 'ContentUtils/constants/TemplateTypes';

const __removeDelimiter = normalizedLayoutData => ({
  widgetId,
  parentId,
  rowId
}) => {
  let {
    widgets,
    rows
  } = normalizedLayoutData;

  if (!widgets[widgetId]) {
    return;
  }

  const {
    x: removedX,
    w: removedW
  } = widgets[widgetId];
  widgets = omit(widgets, widgetId);
  let row = rows[rowId];

  if (row.length === 1 && row[0] === widgetId) {
    // Delimiter was the only widget in row.
    // Remove it from collection and parent widget.
    rows = omit(rows, rowId);
    const parent = widgets[parentId];
    widgets[parentId] = new LayoutWidget(Object.assign({}, parent, {
      rows: without(parent.rows, rowId)
    }));
  } else {
    // Delimiter was NOT the only widget in row.
    // Remove it from collection and adjust sibling widgets to fill space.
    const idx = row.indexOf(widgetId);
    const leftWidgetId = ~idx && idx === 0 ? null : row[idx - 1];
    const rightWidgetId = ~idx && idx === row.length - 1 ? null : row[idx + 1];
    row = without(row, widgetId);
    rows[rowId] = row;

    if (leftWidgetId) {
      // Expand width.
      const widget = widgets[leftWidgetId];
      widgets[leftWidgetId] = new LayoutWidget(Object.assign({}, widget, {
        w: widget.w + removedW
      }));
    } else if (rightWidgetId) {
      // Shift left and expand width.
      const widget = widgets[rightWidgetId];
      widgets[rightWidgetId] = new LayoutWidget(Object.assign({}, widget, {
        x: removedX,
        w: widget.w + removedW
      }));
    }
  }

  normalizedLayoutData.widgets = widgets;
  normalizedLayoutData.rows = rows;
};

const __reformatDelimiter = normalizedLayoutData => ({
  widgetId
}) => {
  const {
    widgets
  } = normalizedLayoutData;

  if (!widgets[widgetId]) {
    return;
  }

  const delimData = new SectionDelimiter().generateNewLayoutWidgetObject();
  widgets[widgetId] = new LayoutWidget(Object.assign({}, delimData, {
    id: widgetId,
    name: widgetId
  }));
  normalizedLayoutData.widgets = widgets;
};

const __insertDelimiter = normalizedLayoutData => () => {
  const {
    widgets,
    rows,
    rootWidgetId
  } = normalizedLayoutData;
  const delimData = new SectionDelimiter().generateNewLayoutWidgetObject();
  const widget = new LayoutWidget(delimData);
  widgets[widget.id] = widget;
  const rowId = createRowId();
  rows[rowId] = [widget.id];
  const rootWidget = widgets[rootWidgetId];
  widgets[rootWidgetId] = new LayoutWidget(Object.assign({}, rootWidget, {
    rows: [rowId, ...rootWidget.rows]
  }));
  normalizedLayoutData.widgets = widgets;
  normalizedLayoutData.rows = rows;
};
/**
 * Determines if a section_delimiter is formatted correctly.
 *
 * @param  {Object}  widgetData
 * @return {Boolean}
 */


export function isSectionDelimiterWellFormed(widgetData) {
  return !!(widgetData.id && widgetData.type === 'section_delimiter' && widgetData.x === 0 && widgetData.w === 12 && // Ensure section_delimiter's are empty.
  // See https://issues.hubspotcentral.com/browse/CG-7769#comment-397262
  widgetData.rows && widgetData.rows.length === 0 && (widgetData.cells == null || widgetData.cells.length === 0) && (widgetData.widgets == null || widgetData.widgets.length === 0));
}
/**
 * Test for valid section_delimiter widget occurence in a layout.
 *
 * @param {NormalizedLayoutData} normalizedLayoutData
 * @param  {TemplateType} templateType   - TemplateType of the layout.
 * @return {LayoutSectionDelimiterValidation}
 */

export function validateSectionDelimiters(normalizedLayoutData, templateType) {
  const {
    widgets,
    rows,
    rootWidgetId
  } = normalizedLayoutData;
  let count = 0;
  const rootDelimiters = [];
  const nestedDelimiters = [];
  const malformedDelimiters = [];
  forEachNormalizedWidgets(widgets, rows, rootWidgetId, (widget, {
    rowId,
    parentId
  }) => {
    if (!isSectionDelimiterType(widget.type)) {
      return;
    }

    ++count;
    const delimiter = {
      widgetId: widget.id,
      rowId,
      parentId
    };

    if (!isSectionDelimiterWellFormed(widget)) {
      malformedDelimiters.push(delimiter);
    }

    const row = rows[rowId]; // A root delimiter is a child of the root widget
    // and is the only widget in the row.

    if (parentId === rootWidgetId && row.length === 1 && row[0] === widget.id) {
      rootDelimiters.push(delimiter);
    } else {
      nestedDelimiters.push(delimiter);
    }
  });
  const isValid = // Global Groups have none
  count === 0 && templateType === byName.global_group.id || // All other layouts have 2 in root
  count === 2 && rootDelimiters.length === 2 && malformedDelimiters.length === 0 && nestedDelimiters.length === 0;
  return {
    isValid,
    count,
    rootDelimiters,
    nestedDelimiters,
    malformedDelimiters
  };
}
/**
 * Corrects any section_delimiter placement issues in normalized layout data.
 *
 * @param  {NormalizedLayoutData} normalizedLayoutData
 * @param  {TemplateType}         templateType
 * @return {NormalizedLayoutData}
 */

export function fixSectionDelimiters(normalizedLayoutData, templateType) {
  const validation = validateSectionDelimiters(normalizedLayoutData, templateType);

  if (validation.isValid) {
    return normalizedLayoutData;
  }

  const layoutData = cloneNormalizedLayoutData(normalizedLayoutData);

  const removeDelimiter = __removeDelimiter(layoutData); // Global Group - remove any/all delimiters.


  if (templateType === byName.global_group.id && validation.count) {
    validation.rootDelimiters.forEach(removeDelimiter);
    validation.nestedDelimiters.forEach(removeDelimiter);
    validation.malformedDelimiters.forEach(removeDelimiter);
    return layoutData;
  }

  const reformatDelimiter = __reformatDelimiter(layoutData);

  const insertDelimiter = __insertDelimiter(layoutData); // Remove all nested delimiters.


  validation.nestedDelimiters.forEach(removeDelimiter); // Reformat malformed root delimiters.
  // Remove all other malformed delimiters.

  validation.malformedDelimiters.forEach(delim => {
    if (~validation.rootDelimiters.indexOf(delim)) {
      reformatDelimiter(delim);
    } else {
      removeDelimiter(delim);
    }
  }); // Insert/remove root delimiters as needed.

  const {
    length: rootCount
  } = validation.rootDelimiters;

  if (rootCount > 2) {
    validation.rootDelimiters.slice(2).forEach(removeDelimiter);
  } else if (rootCount < 2) {
    for (let count = rootCount; count++ < 2;) {
      insertDelimiter();
    }
  }

  return layoutData;
}